import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, CalendarDays, Plus, LogOut, Menu, X, ListChecks, Globe, Mail, MailX, Settings, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePortalTheme } from '../../hooks/usePortalTheme';
import { ThemeToggle } from '../landing/ThemeToggle';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const navItems = [
  { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/app/contacts', label: 'Contacts', icon: Users, testid: 'nav-contacts' },
  { to: '/app/lists', label: 'Lists', icon: ListChecks, testid: 'nav-lists' },
  { to: '/app/events', label: 'Events', icon: CalendarDays, testid: 'nav-events' },
  { to: '/app/landing', label: 'Landing Page', icon: Globe, testid: 'nav-landing' },
  { to: '/app/subscribers', label: 'Subscribers', icon: Mail, testid: 'nav-subscribers' },
  { to: '/app/unsubscribed', label: 'Unsubscribed', icon: MailX, testid: 'nav-unsubscribed' },
  { to: '/app/settings', label: 'Settings', icon: Settings, testid: 'nav-settings' },
  { to: '/app/guide', label: 'Guide', icon: BookOpen, testid: 'nav-guide' },
];

const NavLinks = ({ onNavigate }) => {
  const location = useLocation();
  return (
    <nav className="flex flex-col gap-1">
      {navItems.map((item) => {
        const active = location.pathname.startsWith(item.to);
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={item.testid}
            className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              active
                ? 'bg-secondary text-foreground before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-primary'
                : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
            }`}
          >
            <Icon className={`h-[18px] w-[18px] ${active ? 'text-primary' : ''}`} />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
};

const Brand = () => (
  <Link to="/app/dashboard" className="flex items-center gap-2.5 px-1">
    <img src="/thrive-logo.png" alt="Thrive Gatherings" className="h-9 w-9 rounded-full object-cover ring-1 ring-border" />
    <div className="leading-tight">
      <div className="font-display text-lg font-semibold text-foreground">Thrive</div>
      <div className="text-[11px] tracking-wide text-muted-foreground -mt-1">GATHERINGS</div>
    </div>
  </Link>
);

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { theme, toggle } = usePortalTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[268px] flex-col border-r border-border bg-card px-4 py-5">
        <Brand />
        <div className="mt-8 flex-1">
          <Button
            data-testid="sidebar-create-event-button"
            onClick={() => navigate('/app/events/new')}
            className="w-full h-11 rounded-xl mb-6 gap-2"
          >
            <Plus className="h-4 w-4" /> Create Event
          </Button>
          <NavLinks />
        </div>
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between gap-2 mb-3 px-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Theme</span>
            <ThemeToggle theme={theme} onToggle={toggle} testid="portal-theme-toggle" />
          </div>
          <div className="px-1 mb-3">
            <div className="text-sm font-medium text-foreground truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" data-testid="logout-button" onClick={logout}
            className="w-full justify-start gap-2 text-muted-foreground h-10">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/90 backdrop-blur px-4 h-14">
        <Brand />
        <div className="flex items-center gap-2">
          <ThemeToggle theme={theme} onToggle={toggle} testid="portal-theme-toggle-mobile" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-4">
              <div className="flex items-center justify-between mb-6">
                <Brand />
                <Button variant="ghost" size="icon" onClick={() => setOpen(false)}><X className="h-5 w-5" /></Button>
              </div>
              <Button onClick={() => { setOpen(false); navigate('/app/events/new'); }} className="w-full h-11 rounded-xl mb-6 gap-2">
                <Plus className="h-4 w-4" /> Create Event
              </Button>
              <NavLinks onNavigate={() => setOpen(false)} />
              <div className="border-t border-border mt-6 pt-4">
                <Button variant="ghost" onClick={logout} className="w-full justify-start gap-2 text-muted-foreground">
                  <LogOut className="h-4 w-4" /> Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="lg:pl-[268px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</div>
      </main>
    </div>
  );
};
